import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { hives as hivesApi, feed as feedApi, settings as settingsApi } from '../utils/api';

const FeedContext = createContext(null);

const CACHE_TIMEOUT = 30000; // 30 seconds - show cached data, refetch in background

export function FeedProvider({ children }) {
  // Cached hive data (full structure from getMyHives)
  const [hiveData, setHiveData] = useState({
    active: [],           // For filter bar (sorted by activity)
    all_hives: [],        // For My Hives tab "All" filter
    my_created: [],       // Hives I created
    joined: [],           // Hives I joined
    private: [],          // Private hives only
    public: [],           // Public hives only
    dormant: [],          // Dormant hives
    unread_count: 0,
    unread_private_count: 0
  });
  
  const [popularHives, setPopularHives] = useState([]);
  const [appSettings, setAppSettings] = useState({ public_hives_available: false, is_admin: false });
  const [feedPosts, setFeedPosts] = useState({}); // keyed by `${hiveId}_${sort}`
  const [hiveDetails, setHiveDetails] = useState({}); // keyed by hiveId - { hive, posts, members }
  
  // Timestamps for cache invalidation
  const cacheTimestamps = useRef({
    hiveData: 0,
    popularHives: 0,
    appSettings: 0,
    feedPosts: {},
    hiveDetails: {}
  });
  
  // Loading states (only true for INITIAL load, not background refresh)
  const [loading, setLoading] = useState({
    hiveData: false,
    popularHives: false,
    feedPosts: false
  });
  
  // Track if we've ever loaded data (to distinguish first load from refresh)
  const hasLoadedOnce = useRef({
    hiveData: false,
    popularHives: false,
    appSettings: false
  });

  const isCacheValid = (key, subKey = null) => {
    const now = Date.now();
    if (subKey) {
      return (cacheTimestamps.current[key]?.[subKey] || 0) > now - CACHE_TIMEOUT;
    }
    return (cacheTimestamps.current[key] || 0) > now - CACHE_TIMEOUT;
  };

  const updateCacheTimestamp = (key, subKey = null) => {
    const now = Date.now();
    if (subKey) {
      cacheTimestamps.current[key] = cacheTimestamps.current[key] || {};
      cacheTimestamps.current[key][subKey] = now;
    } else {
      cacheTimestamps.current[key] = now;
    }
  };

  // Fetch My Hives with caching - returns full structure
  const fetchHiveData = useCallback(async (forceRefresh = false) => {
    const hasCachedData = hasLoadedOnce.current.hiveData;
    const cacheValid = isCacheValid('hiveData');
    
    // Return cached data immediately if available and valid
    if (hasCachedData && !forceRefresh && cacheValid) {
      return hiveData;
    }
    
    // Only show loading spinner on FIRST load, not background refresh
    if (!hasCachedData) {
      setLoading(prev => ({ ...prev, hiveData: true }));
    }
    
    try {
      const res = await hivesApi.getMyHives();
      
      // Handle new response format with all hive types
      let newData;
      if (res.data.active !== undefined) {
        newData = {
          active: res.data.active || [],
          all_hives: res.data.all_hives || res.data.active || [],
          my_created: res.data.my_created || [],
          joined: res.data.joined || [],
          private: res.data.private || [],
          public: res.data.public || [],
          dormant: res.data.dormant || [],
          unread_count: res.data.unread_count || 0,
          unread_private_count: res.data.unread_private_count || 0
        };
      } else {
        // Fallback for old format
        newData = {
          active: res.data || [],
          all_hives: res.data || [],
          my_created: [],
          joined: [],
          private: [],
          public: [],
          dormant: [],
          unread_count: 0,
          unread_private_count: 0
        };
      }
      
      setHiveData(newData);
      updateCacheTimestamp('hiveData');
      hasLoadedOnce.current.hiveData = true;
      return newData;
    } catch (error) {
      console.error('Failed to fetch hive data:', error);
      return hiveData;
    } finally {
      setLoading(prev => ({ ...prev, hiveData: false }));
    }
  }, [hiveData]);

  // Fetch Popular Hives with caching
  const fetchPopularHives = useCallback(async (forceRefresh = false) => {
    const hasCachedData = hasLoadedOnce.current.popularHives;
    const cacheValid = isCacheValid('popularHives');
    
    if (hasCachedData && !forceRefresh && cacheValid) {
      return popularHives;
    }
    
    if (!hasCachedData) {
      setLoading(prev => ({ ...prev, popularHives: true }));
    }
    
    try {
      const res = await hivesApi.getPopular(10);
      const data = res.data || [];
      setPopularHives(data);
      updateCacheTimestamp('popularHives');
      hasLoadedOnce.current.popularHives = true;
      return data;
    } catch (error) {
      console.error('Failed to fetch popular hives:', error);
      return popularHives;
    } finally {
      setLoading(prev => ({ ...prev, popularHives: false }));
    }
  }, [popularHives]);

  // Fetch App Settings with caching
  const fetchAppSettings = useCallback(async (forceRefresh = false) => {
    const hasCachedData = hasLoadedOnce.current.appSettings;
    const cacheValid = isCacheValid('appSettings');
    
    if (hasCachedData && !forceRefresh && cacheValid) {
      return appSettings;
    }
    
    try {
      const res = await settingsApi.get();
      if (res.data.success) {
        const newSettings = {
          public_hives_available: res.data.effective?.public_hives_available || false,
          is_admin: res.data.is_admin || false
        };
        setAppSettings(newSettings);
        updateCacheTimestamp('appSettings');
        hasLoadedOnce.current.appSettings = true;
        return newSettings;
      }
      return appSettings;
    } catch (error) {
      console.error('Failed to fetch app settings:', error);
      return appSettings;
    }
  }, [appSettings]);

  // Fetch Feed Posts with caching
  const fetchFeedPosts = useCallback(async (hiveId = null, sort = 'recent', forceRefresh = false) => {
    const cacheKey = `${hiveId || 'all'}_${sort}`;
    const hasCachedData = feedPosts[cacheKey] !== undefined;
    const cacheValid = isCacheValid('feedPosts', cacheKey);
    
    if (hasCachedData && !forceRefresh && cacheValid) {
      return feedPosts[cacheKey];
    }
    
    // Only show loading on first load of this particular cache key
    if (!hasCachedData) {
      setLoading(prev => ({ ...prev, feedPosts: true }));
    }
    
    try {
      const res = await feedApi.get(hiveId, sort);
      const data = res.data || [];
      setFeedPosts(prev => ({ ...prev, [cacheKey]: data }));
      updateCacheTimestamp('feedPosts', cacheKey);
      return data;
    } catch (error) {
      console.error('Failed to fetch feed posts:', error);
      return feedPosts[cacheKey] || [];
    } finally {
      setLoading(prev => ({ ...prev, feedPosts: false }));
    }
  }, [feedPosts]);

  // Get cached feed posts for a specific key (without fetching)
  const getCachedFeedPosts = useCallback((hiveId = null, sort = 'recent') => {
    const cacheKey = `${hiveId || 'all'}_${sort}`;
    return feedPosts[cacheKey];
  }, [feedPosts]);

  // Fetch and cache hive detail (hive info + posts + members)
  const fetchHiveDetail = useCallback(async (hiveId, forceRefresh = false) => {
    const cached = hiveDetails[hiveId];
    const cacheValid = isCacheValid('hiveDetails', hiveId);
    
    // Return cached data immediately if valid
    if (cached && !forceRefresh && cacheValid) {
      return cached;
    }
    
    try {
      // Fetch all hive data in parallel
      const [hiveRes, postsRes, membersRes] = await Promise.all([
        hivesApi.getById(hiveId),
        hivesApi.getPosts(hiveId),
        hivesApi.getMembers(hiveId)
      ]);
      
      const detail = {
        hive: hiveRes.data,
        posts: postsRes.data || [],
        members: membersRes.data || []
      };
      
      setHiveDetails(prev => ({ ...prev, [hiveId]: detail }));
      updateCacheTimestamp('hiveDetails', hiveId);
      return detail;
    } catch (error) {
      console.error('Failed to fetch hive detail:', error);
      return cached || null;
    }
  }, [hiveDetails]);

  // Get cached hive detail (without fetching)
  const getCachedHiveDetail = useCallback((hiveId) => {
    return hiveDetails[hiveId];
  }, [hiveDetails]);

  // Update hive detail posts in cache
  const updateHiveDetailPosts = useCallback((hiveId, posts) => {
    setHiveDetails(prev => {
      if (!prev[hiveId]) return prev;
      return { ...prev, [hiveId]: { ...prev[hiveId], posts } };
    });
  }, []);

  // Update feed posts in cache (for optimistic updates like likes/comments)
  const updateFeedPostInCache = useCallback((buzzId, updates) => {
    setFeedPosts(prev => {
      const newFeedPosts = { ...prev };
      Object.keys(newFeedPosts).forEach(key => {
        newFeedPosts[key] = newFeedPosts[key].map(post => 
          post.buzz_id === buzzId ? { ...post, ...updates } : post
        );
      });
      return newFeedPosts;
    });
  }, []);

  // Update hive unread status in cache
  const updateHiveUnreadStatus = useCallback((hiveId, hasUnread) => {
    setHiveData(prev => ({
      ...prev,
      active: prev.active.map(h => h.hive_id === hiveId ? { ...h, has_unread: hasUnread } : h),
      all_hives: prev.all_hives.map(h => h.hive_id === hiveId ? { ...h, has_unread: hasUnread } : h)
    }));
  }, []);

  // Invalidate cache for a specific hive (e.g., after posting)
  const invalidateHiveCache = useCallback((hiveId) => {
    // Clear feed post timestamps to force refresh
    if (cacheTimestamps.current.feedPosts) {
      Object.keys(cacheTimestamps.current.feedPosts).forEach(key => {
        if (key.startsWith('all_') || key.startsWith(`${hiveId}_`) || key === `null_recent` || key === `null_trending`) {
          cacheTimestamps.current.feedPosts[key] = 0;
        }
      });
    }
    // Clear hive detail cache
    if (cacheTimestamps.current.hiveDetails) {
      cacheTimestamps.current.hiveDetails[hiveId] = 0;
    }
    cacheTimestamps.current.hiveData = 0;
  }, []);

  // Clear all cache (e.g., on logout)
  const clearCache = useCallback(() => {
    setHiveData({
      active: [],
      all_hives: [],
      my_created: [],
      joined: [],
      private: [],
      public: [],
      dormant: [],
      unread_count: 0,
      unread_private_count: 0
    });
    setPopularHives([]);
    setFeedPosts({});
    setHiveDetails({});
    cacheTimestamps.current = {
      hiveData: 0,
      popularHives: 0,
      appSettings: 0,
      feedPosts: {},
      hiveDetails: {}
    };
    hasLoadedOnce.current = {
      hiveData: false,
      popularHives: false,
      appSettings: false
    };
  }, []);

  const value = {
    // Data
    hiveData,
    popularHives,
    appSettings,
    feedPosts,
    
    // Loading states
    loading,
    
    // Fetch functions
    fetchHiveData,
    fetchPopularHives,
    fetchAppSettings,
    fetchFeedPosts,
    getCachedFeedPosts,
    fetchHiveDetail,
    getCachedHiveDetail,
    
    // Update functions
    updateFeedPostInCache,
    updateHiveUnreadStatus,
    updateHiveDetailPosts,
    invalidateHiveCache,
    clearCache,
    
    // Direct setters for edge cases
    setHiveData,
    setFeedPosts,
    setHiveDetails
  };

  return (
    <FeedContext.Provider value={value}>
      {children}
    </FeedContext.Provider>
  );
}

export function useFeedContext() {
  const context = useContext(FeedContext);
  if (!context) {
    throw new Error('useFeedContext must be used within a FeedProvider');
  }
  return context;
}

export default FeedContext;
